import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { isBoolean } from 'lodash-es';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { Option } from '@shared/types/components.type';

type OptionalFilters = {
  does_invoice_mappings_match_total: boolean;
  has_prepaid: boolean;
  is_deposit: boolean;
  purchase_order: boolean;
  services_period: boolean;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    NgSelectModule,
    TooltipDirective,
    SlicePipe,
    MultiSelectDropdownComponent,
    CdkOverlayOrigin,
    ClickOutsideDirective,
    CdkConnectedOverlay,
    ButtonComponent,
  ],
  selector: 'aux-invoice-filters',
  styleUrls: ['./invoice-filters.component.scss'],
  templateUrl: './invoice-filters.component.html',
})
export class InvoiceFiltersComponent implements OnInit {
  readonly invoiceService = inject(InvoiceService);
  readonly launchDarklyService = inject(LaunchDarklyService);

  private destroyRef = inject(DestroyRef);

  isAddFiltersOpen = signal(false);
  optionalFilters = signal<OptionalFilters>({
    does_invoice_mappings_match_total: false,
    has_prepaid: false,
    is_deposit: false,
    purchase_order: false,
    services_period: false,
  });

  depositFilterFF = this.launchDarklyService.$select((flags) => flags.invoices_deposits);
  prepaidFilterFF = this.launchDarklyService.$select((flags) => flags.prepaids);

  optionalFiltersFeatureFlags = computed<{ [key: string]: boolean }>(() => ({
    does_invoice_mappings_match_total: true,
    has_prepaid: this.prepaidFilterFF(),
    is_deposit: this.depositFilterFF(),
    purchase_order: true,
    services_period: true,
  }));
  showAddFilter = computed(() => {
    return Object.entries(this.optionalFilters()).some(
      ([key, value]) => !value && this.optionalFiltersFeatureFlags()[key]
    );
  });

  editMode = input.required<boolean>();

  depositItems = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  @Input() form!: FormGroup;
  mappedToActivitiesItems = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  prepaidItems = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  private onAddFilterHide = effect(() => {
    if (!this.showAddFilter()) {
      this.isAddFiltersOpen.set(false);
    }
  });
  private onFeatureFlagsChange = effect(() => {
    Object.entries(this.optionalFiltersFeatureFlags()).forEach(([optionKey, featureFlag]) => {
      if (!featureFlag) {
        untracked(this.onRemoveOptionalFilter(optionKey as keyof OptionalFilters));
      }
    });
  });

  ngOnInit(): void {
    if (isBoolean(this.form.get('is_deposit')?.value)) {
      this.optionalFilters.update((v) => ({
        ...v,
        is_deposit: this.optionalFiltersFeatureFlags()['is_deposit'],
      }));
    }

    if (isBoolean(this.form.get('has_prepaid')?.value)) {
      this.optionalFilters.update((v) => ({
        ...v,
        has_prepaid: this.optionalFiltersFeatureFlags()['has_prepaid'],
      }));
    }

    if (isBoolean(this.form.get('does_invoice_mappings_match_total')?.value)) {
      this.optionalFilters.update((v) => ({
        ...v,
        does_invoice_mappings_match_total: true,
      }));
    }

    this.initializeServicePeriod();
    this.initializePurchaseOrder();
  }

  castItemsToOptions(items: unknown): Option[] {
    return Array.isArray(items) ? items : [];
  }

  initializePurchaseOrder() {
    this.form
      .get('purchase_order')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => this.invoiceService.setSelectedPurchaseOrder(val));

    const options = this.invoiceService.selectedPurchaseOrder();

    if (options && options.length) {
      this.form.get('purchase_order')?.setValue(options);
      this.optionalFilters.set({ ...this.optionalFilters(), purchase_order: true });
    }
  }

  initializeServicePeriod() {
    this.form
      .get('services_period')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.invoiceService.setSelectedServicePeriodOptions(val);
      });
    const options = this.invoiceService.selectedServicePeriodOptions();

    if (options && options.length) {
      this.form.get('services_period')?.setValue(options);
      this.optionalFilters.set({ ...this.optionalFilters(), services_period: true });
    }
  }

  onAddOptionalFilter = (key: keyof OptionalFilters) => {
    this.optionalFilters.update((v) => ({ ...v, [key]: true }));
    this.isAddFiltersOpen.set(false);
  };

  onRemoveOptionalFilter = (key: keyof OptionalFilters) => () => {
    this.form.get(key)?.reset();
    this.optionalFilters.update((v) => ({ ...v, [key]: false }));
  };

  resetAllFilters() {
    Object.values(this.form.controls).forEach((control) => {
      control.reset();
    });
  }
}
